import { useEffect } from 'react';
import Lenis from 'lenis';

export interface SmoothScrollOptions {
    /** Higher = more glide. Lenis default is 0.1; the corridor wants a touch more. */
    lerp?: number;
    /** Multiplier on wheel delta */
    wheelMultiplier?: number;
    /** Turn the whole thing off without unmounting the caller */
    enabled?: boolean;
}

/**
 * Smooth scrolling for one route, via Lenis.
 *
 * Deliberately NOT applied site-wide. Hijacking the scroll of an article page
 * fights the reader's own muscle memory and their browser's find-on-page; the
 * only place it earns its keep here is the home corridor, where scroll position
 * drives a camera and the raw wheel signal is too steppy to fly with.
 *
 * Lenis keeps `window.scrollY` authoritative, so anything already reading
 * scroll position keeps working unchanged — it just receives interpolated
 * values instead of stepped ones.
 *
 * Disabled under `prefers-reduced-motion`: a visitor who asked for less motion
 * did not ask for their scrollbar to acquire momentum.
 */
export function useSmoothScroll({
    lerp = 0.085,
    wheelMultiplier = 1,
    enabled = true,
}: SmoothScrollOptions = {}): void {
    useEffect(() => {
        if (!enabled) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        // Coarse pointers already have native momentum; adding ours fights it.
        if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) return;

        const lenis = new Lenis({
            lerp,
            wheelMultiplier,
            // Leave touch alone — see above.
            syncTouch: false,
        });

        let raf = 0;
        const loop = (time: number) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, [lerp, wheelMultiplier, enabled]);
}
